import React, { useState } from 'react';
import { Cookie } from 'lucide-react';
import { setAnalyticsConsent } from '../services/analytics';

interface Props {
  /** Called once a choice is made so the host can stop rendering the banner. */
  onDecided: () => void;
}

/**
 * The analytics consent banner. Only rendered for NON-student visitors who haven't
 * chosen yet (see analytics.ts) — students are never tracked and never asked.
 *
 * Deliberately symmetric: "Decline" is as prominent as "Allow". Under GDPR consent
 * must be freely given, so a nagging or lopsided banner is worse than none — and
 * there's no dismiss-without-choosing, because closing a banner isn't consent.
 */
export const AnalyticsConsentBanner: React.FC<Props> = ({ onDecided }) => {
  const [saving, setSaving] = useState(false);

  const choose = (choice: 'granted' | 'denied') => {
    setSaving(true);
    setAnalyticsConsent(choice);
    onDecided();
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[70] p-3 sm:p-4">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-2xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <Cookie size={20} className="text-gray-400 flex-shrink-0 hidden sm:block" />
        <p className="text-sm text-gray-700 flex-1">
          We'd like to use analytics and ad measurement to see what's working. It's optional — Level Up works exactly the same either way, and we never track student accounts or send student data to advertisers.
        </p>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => choose('denied')}
            disabled={saving}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl"
          >
            Decline
          </button>
          <button
            onClick={() => choose('granted')}
            disabled={saving}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-xl"
          >
            Allow
          </button>
        </div>
      </div>
    </div>
  );
};
